import { Button } from 'primeng/button';
import { Message } from 'primeng/message';
import { Tooltip } from 'primeng/tooltip';
import { Textarea } from 'primeng/textarea';
import { Fieldset } from 'primeng/fieldset';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { FieldInvalidPipe } from '../../pipes/field-invalid.pipe';
import { SupportService } from '../../../core/services/support.service';
import { NotificationService } from '../../../core/services/notification.service';
import { nonEmptyStringValidator } from '../../validators/non-empty-string.validator';

@Component({
	selector: 'app-support-request-form',
	templateUrl: './support-request-form.component.html',
	imports: [CommonModule, ReactiveFormsModule, Button, Message, Textarea, Fieldset, FieldInvalidPipe, Tooltip],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupportRequestFormComponent {
	public showCancelButton = input<boolean>(true);
	public customUrl = input<string | null>(null);

	public submitted = output<void>();
	public cancelled = output<void>();

	public isLoading = signal(false);

	private readonly fb = inject(FormBuilder);
	private readonly supportService = inject(SupportService);
	private readonly notificationService = inject(NotificationService);

	/**
	 * The form group for the support request.
	 */
	public supportForm: FormGroup<{ message: FormControl<string> }> = this.fb.nonNullable.group({
		message: this.fb.nonNullable.control('', [Validators.required, nonEmptyStringValidator({ minLength: 1, maxLength: 5000 })])
	});

	/**
	 * Resets the form to its initial state.
	 */
	public resetForm(): void {
		this.supportForm.reset();
	}

	/**
	 * Handles cancel button click.
	 */
	public onCancel(): void {
		this.resetForm();
		this.cancelled.emit();
	}

	/**
	 * Submits the support request to the backend.
	 */
	public submitRequest(): void {
		if (this.supportForm.invalid) return;

		this.isLoading.set(true);
		const message = this.supportForm.get('message')?.value || '';
		const url = this.customUrl() || window.location.href;

		this.supportService.sendRequest({ message, url }).subscribe({
			next: () => {
				this.notificationService.success('Your support request has been sent successfully.');
				this.isLoading.set(false);
				this.resetForm();
				this.submitted.emit();
			},
			error: () => {
				this.notificationService.error('Failed to send support request. Please try again.');
				this.isLoading.set(false);
			}
		});
	}
}
